<!-- Üksiku e-poe toote vaheleht -->
<template>
  <v-sheet class="page-properties default-padding">
    <page-header></page-header>
    <v-sheet class="mx-auto" v-if="isLoading">
      <v-skeleton-loader type="heading, image, paragraph"></v-skeleton-loader>
    </v-sheet>
    <v-sheet v-else class="d-flex content-body ga-5"> 
        <v-sheet class="d-flex w-100 align-center justify-center flex-wrap">
          <v-carousel
              v-if="product.image?.length"
              class="carousel-height view-product-carousel"
              v-model="mainCarouselIndex"
              show-arrows="hover"
              hide-delimiters
              hide-delimiter-background
              :cycle="false"
          >
            <v-carousel-item v-for="(img,i) in product.image" :key="i" class="view-product-carousel-item">
              <v-img :src="img" class="view-product-image" contain @click="openImageOverlay(i)"></v-img>
            </v-carousel-item>
          </v-carousel>
          <div v-if="product.image?.length > 1" class="thumbnail-gallery">
            <button
                v-for="(img, i) in product.image"
                :key="`thumb-${i}`"
                type="button"
                class="thumbnail-btn"
                :class="{ 'thumbnail-btn-active': mainCarouselIndex === i }"
                :aria-label="`Show image ${i + 1}`"
                @click="mainCarouselIndex = i"
            >
              <v-img :src="img" class="thumbnail-image" cover></v-img>
            </button>
          </div>
          <img v-if="!product.image?.length" class="pa-5" src="../../assets/images/lohnakas.png" alt="Lohnakas logo"/>
      </v-sheet>
      <v-overlay v-model="dialog" class="image-overlay" :scrim="true" @click:outside="closeImageOverlay">
        <div class="overlay-content">
          <v-btn
              class="overlay-close"
              icon="mdi-close"
              variant="text"
              color="white"
              @click="closeImageOverlay"
          ></v-btn>
          <v-carousel
              v-if="product.image?.length"
              v-model="fullscreenIndex"
              class="overlay-carousel"
              show-arrows="hover"
              hide-delimiters
              hide-delimiter-background
              :cycle="false"
          >
            <v-carousel-item
                v-for="(img, i) in product.image"
                :key="`overlay-${i}`"
                class="overlay-carousel-item"
            >
              <v-img
                  :src="img"
                  class="overlay-image"
                
                  contain
              ></v-img>
            </v-carousel-item>
          </v-carousel>
          <div v-if="product.image?.length > 1" class="thumbnail-gallery overlay-thumbnail-gallery">
            <button
                v-for="(img, i) in product.image"
                :key="`overlay-thumb-${i}`"
                type="button"
                class="thumbnail-btn overlay-thumbnail-btn"
                :class="{ 'thumbnail-btn-active': fullscreenIndex === i }"
                :aria-label="`Show fullscreen image ${i + 1}`"
                @click="fullscreenIndex = i"
            >
              <v-img :src="img" class="thumbnail-image" cover></v-img>
            </button>
          </div>
        </div>
      </v-overlay>
      <v-sheet class="rounded-lg order-summary" elevation="5">
        <div class="d-flex align-center flex-1-1 pa-5">
          <div class="d-flex text-left flex-column w-100">
            <h2>{{ getHeader(product) }}</h2>
            <div class="cat-link" v-if="getProductCategoryGroups(product).length">
              <template v-for="(group, groupIndex) in getProductCategoryGroups(product)" :key="`product-category-group-${groupIndex}`">
                <span class="product-category-group">
                      <template v-for="(part, index) in group.pathParts" :key="`product-category-part-${groupIndex}-${index}`">
                        <router-link :to="`/tooted/${getSlugForPathParts(group.pathParts, index)}`" :class="['product-category-level-link', index === 0 ? 'product-category-main' : 'product-category-sub']" @click="resetCategoryPage">
                          <span>{{ part }}</span>
                        </router-link>
                        <span v-if="index < group.pathParts.length - 1" class="product-category-arrow">›</span>
                      </template>
                      <span v-if="group.leafLabels.length && group.pathParts.length" class="product-category-arrow">›</span>
                      <template v-for="(leaf, leafIndex) in group.leafLabels" :key="`product-category-leaf-${groupIndex}-${leafIndex}`">
                        <router-link :to="`/tooted/${getSlugForLeaf(group, leaf)}`" class="product-category-level-link product-category-sub product-category-extra" @click="resetCategoryPage">
                          <span>{{ leaf }}</span>
                        </router-link>
                      </template>
                </span>
              </template>
            </div>
            <div class="tiptap tiptap-editor py-3" v-html=getBody(product)></div>
            <div class="purchase-config">
              <div class="config-card">
                <div class="config-label">{{ $t('valige_kogus') }}</div>
                <div class="quantity-picker">
                  <v-btn color="eraklient" @click="decreaseQuantity" icon="mdi-minus" size="30px" aria-label="Decrease Quantity"><v-icon size="20px"></v-icon></v-btn>
                  <div class="quantity-input-wrap">
                    <input class="quantity-input" :title="$t('valige_kogus')" v-model.number="quantity" type="text" inputmode="numeric" pattern="[0-9]*">
                  </div>
                  <v-btn color="eraklient" @click="increaseQuantity" icon="mdi-plus" size="30px" aria-label="Increase Quantity"><v-icon size="20px"></v-icon></v-btn>
                </div>
              </div>
              <div v-if="hasSprayRollOnOption(product)" class="config-card">
                <div class="config-label">{{ $t('lohna_tuup') }}</div>
                <div class="option-toggle" role="radiogroup" :aria-label="$t('lohna_tuup')">
                  <button type="button" :class="['option-pill', { 'option-pill--active': choice === 'Spray', 'option-pill--disabled': !isSprayAvailable(product) }]" :aria-pressed="choice === 'Spray'" :disabled="!isSprayAvailable(product)" @click="!isSprayAvailable(product) ? null : (choice = 'Spray', choiceError = false)">
                    Spray
                  </button>
                  <button type="button" :class="['option-pill', { 'option-pill--active': choice === 'Roll-on', 'option-pill--disabled': !isRollOnAvailable(product) }]" :aria-pressed="choice === 'Roll-on'" :disabled="!isRollOnAvailable(product)" @click="!isRollOnAvailable(product) ? null : (choice = 'Roll-on', choiceError = false)">
                    Roll-on
                  </button>
                </div>
                <div v-if="choiceError" class="config-error">{{ $t('vali_lohna_tuup_error') }}</div>
              </div>
              <div v-if="hasBottleSizeOption(product)" class="config-card">
                <div class="config-label">{{ $t('vali_pudeli_suurus') }}</div>
                <div class="option-toggle" role="radiogroup" :aria-label="$t('vali_pudeli_suurus')">
                  <button
                    v-for="bs in getBottleSizes(product)"
                    :key="bs.size"
                    type="button"
                    :class="['option-pill', { 'option-pill--active': size === bs.size, 'option-pill--disabled': bs.disabled }]"
                    :aria-pressed="size === bs.size"
                    :disabled="bs.disabled"
                    @click="bs.disabled ? null : selectBottleSize(bs.size)"
                  >{{ bs.size }}</button>
                </div>
                <div v-if="bottleSizeError" class="config-error">{{ $t('vali_pudeli_suurus_error') }}</div>
              </div>
            </div>
            <div class="purchase-footer">
              <div class="purchase-price-block">
                <div class="purchase-price-label">{{ getPurchasePriceTitle() }}</div>
                <div class="purchase-price-row">
                  <template v-if="hasBottleSizePriceRange(product) && !size">
                    <span class="purchase-price-range">{{ getPriceLabel(product, size) }}</span>
                  </template>
                  <template v-else-if="product.new_price && !hasBottleSizeOption(product)">
                    <span class="purchase-price-old">{{ formatPrice(product.price) }}</span>
                    <span class="purchase-price-value purchase-price-value--discount">{{ formatPrice(product.new_price) }}</span>
                    <span class="purchase-price-currency purchase-price-currency--discount">EUR</span>
                  </template>
                  <template v-else>
                    <span class="purchase-price-value">{{ formatPrice(getSelectedUnitPrice(product, size)) }}</span>
                    <span class="purchase-price-currency">EUR</span>
                  </template>
                </div>
              </div>
            </div>
            <v-dialog v-model="valid" width="auto" transition="dialog-bottom-transition" persistent>
              <v-card class="confirmation-dialog-card">
                <v-toolbar color="white" :title="$t('kinnitus')">
                  <v-btn icon="mdi-close" @click.stop="valid = false"></v-btn>
                </v-toolbar>
                <v-card-text class="pa-4">
                  <div class="confirmation-header">
                    <span class="confirmation-kicker">{{ $t('kinnitus_conf') }}</span>
                  </div>
                  <div class="confirmation-summary">
                    <div class="summary-product-chip">{{ getHeader(product) }}</div>
                    <div class="summary-topline">
                      <span class="summary-label">{{ $t('valige_kogus') }}</span>
                      <span class="summary-chip summary-chip--count">{{ quantity }}</span>
                    </div>
                    <div v-if="hasSprayRollOnOption(product) || hasBottleSizeOption(product)" class="summary-meta">
                      <div v-if="hasSprayRollOnOption(product)" class="summary-row">
                        <span class="summary-label">{{ $t('lohna_tuup') }}</span>
                        <span class="summary-chip">{{ getChoiceLabel() }}</span>
                      </div>
                      <div v-if="hasBottleSizeOption(product)" class="summary-row">
                        <span class="summary-label">{{ $t('vali_pudeli_suurus') }}</span>
                        <span class="summary-chip">{{ getSizeLabel() }}</span>
                      </div>
                    </div>
                    <div class="summary-price-pill summary-price-pill--bottom">
                      <span class="summary-price-value">{{ formatPrice(getDialogTotal(product, size)) }}</span>
                      <span class="summary-price-currency">EUR</span>
                    </div>
                  </div>
                </v-card-text>
                <v-divider></v-divider>
                <div class="pa-4">
                    <v-btn class="text-none mr-4 font-weight-bold" color="eraklient" min-width="92" rounded type="button" @click.stop.prevent="confirmAddToCart">{{ $t('lisa_korvi') }}</v-btn>
                </div>
              </v-card>
            </v-dialog>
            <v-btn class="cart-btn product-cart-btn" color="eraklient" @click.stop.prevent="openAddToCartDialog" aria-haspopup="true" :aria-expanded="valid">{{ $t('lisa_korvi') }}</v-btn>
          </div>
        </div>
      </v-sheet>
    </v-sheet>
  </v-sheet>
</template>


<script>
import PageHeader from "@/components/PageHeader";
import {toast} from "vue3-toastify";
import { formatPriceValue, getProductPriceLabel as resolveProductPriceLabel, getProductUnitPrice, hasBottleSizePriceRange as productHasBottleSizePriceRange, isBottleSizeOptionEnabled, isSprayAvailable, isRollOnAvailable, isSprayRollOnOptionEnabled, parseBottleSizes } from "@/utils/productOptions.js";

export default {
  name: "ViewProduct",
  components: {PageHeader},
  data() {
    return {
      product: [],
      categories: this.$store.state.categories,
      quantity: 1,
      isLoading: true,
      valid: false,
      dialog: false,
      mainCarouselIndex: 0,
      fullscreenIndex: 0,
      choice: null,
      size: null,
      bottleSizeError: false,
      choiceError: false
    };
  },
  methods: {
    resetCategoryPage() {
      sessionStorage.setItem('currentPageProducts', 1);
    },
    slugifyCategory(value) {
      const parts = String(value || '')
          .split(' - ')
          .map((part) => part.trim())
          .filter(Boolean);

      const slugifyPart = (part) => part
          .toLowerCase()
          .replace(/[ä]/g, 'a')
          .replace(/[öõ]/g, 'o')
          .replace(/[ü]/g, 'u')
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');

      return parts.map(slugifyPart).filter(Boolean).join('/');
    },
    fetchAProduct(id) {
      fetch(`${process.env.VUE_APP_SERVER_URL}/api/products/${id}`)
          .then((response) => response.json())
          .then((data) => {
            this.product = data;
            this.choice = null;
            this.size = this.hasBottleSizeOption(data) ? null : null;
            this.bottleSizeError = false;
            this.choiceError = false;
            this.isLoading = false;
          })
          .catch((err) => console.log(err.message));
    },
    increaseQuantity() {
      this.quantity++;
    },
    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    addToCart() {
      const choice = this.hasSprayRollOnOption(this.product) ? this.choice : null;
        const size = this.hasBottleSizeOption(this.product) ? this.size : null;

        if (this.hasSprayRollOnOption(this.product) && !choice) {
          this.choiceError = true;
          return false;
        }

        if (this.hasBottleSizeOption(this.product) && !size) {
          this.bottleSizeError = true;
          return false;
        }

      this.$store.dispatch('addToCart', { product: this.product, quantity: this.quantity, choice, size });
      this.quantity = 1;
      this.choice = null;
      this.size = null;
      this.bottleSizeError = false;
      this.choiceError = false;
        return true;
      },
      openAddToCartDialog() {
        if (this.hasSprayRollOnOption(this.product) && !this.choice) {
          this.choiceError = true;
          return;
        }

        if (this.hasBottleSizeOption(this.product) && !this.size) {
          this.bottleSizeError = true;
          return;
        }

        this.valid = true;
      },
      selectBottleSize(size) {
        this.size = size;
        this.bottleSizeError = false;
      },
      confirmAddToCart() {
        if (this.addToCart()) {
          this.valid = false;
          this.showToastSuccess(this.$t('lisatud_korvi'));
        }
    },
    hasSprayRollOnOption(content) {
      return isSprayRollOnOptionEnabled(content);
    },
    isSprayAvailable(content) {
      return isSprayAvailable(content);
    },
    isRollOnAvailable(content) {
      return isRollOnAvailable(content);
    },
    hasBottleSizeOption(content) {
      return isBottleSizeOptionEnabled(content);
    },
    getBottleSizes(content) {
      return parseBottleSizes(content);
    },
    hasBottleSizePriceRange(content) {
      return productHasBottleSizePriceRange(content);
    },
    getChoiceLabel() {
      return this.choice || 'Spray';
    },
    getSizeLabel() {
      return this.size || '-';
    },
    getPriceLabel(content, size) {
      return resolveProductPriceLabel(content, size);
    },
    getSelectedUnitPrice(content, size) {
      return getProductUnitPrice(content, size);
    },
    getDialogTotal(content, size) {
      return getProductUnitPrice(content, size) * Number(this.quantity || 0);
    },
    formatPrice(value) {
      return formatPriceValue(value);
    },
    getPurchasePriceTitle() {
      if (this.$i18n.locale === 'en') return 'PRICE';
      if (this.$i18n.locale === 'ru') return 'ЦЕНА';
      return 'HIND';
    },
    getHeader(content) {
      return (this.$i18n.locale === 'en' && content.header_en !== null) ? content.header_en : (this.$i18n.locale === 'ru' && content.header_ru !== null) ? content.header_ru : content.header;
    },
    getBody(content) {
      return (this.$i18n.locale === 'en' && content.description_en !== null) ? content.description_en : (this.$i18n.locale === 'ru' && content.description_ru !== null) ? content.description_ru : content.description;
    },
    getCategoryLabelById(categoryId) {
      const category = this.categories.find((cat) => cat.id === categoryId);
      if (!category) return '';

      const categoryName = (this.$i18n.locale === 'en' && category.name_en !== null)
          ? category.name_en
          : (this.$i18n.locale === 'ru' && category.name_ru !== null)
              ? category.name_ru
              : category.name;

      return String(categoryName || '').trim();
    },
    getCategorySlugById(categoryId) {
      const category = this.categories.find((cat) => cat.id === categoryId);
      if (!category) return '';

      const categoryName = (this.$i18n.locale === 'en' && category.name_en !== null)
          ? category.name_en
          : (this.$i18n.locale === 'ru' && category.name_ru !== null)
              ? category.name_ru
              : category.name;

      const firstPart = String(categoryName || '')
          .split(' - ')
          .map((part) => part.trim())
          .filter(Boolean)[0] || '';

      return this.slugifyCategory(firstPart);
    },
    getSlugForPathParts(pathParts, index) {
      const parts = (pathParts || []).slice(0, (index == null ? pathParts.length : index + 1));
      const label = parts.join(' - ');
      return this.slugifyCategory(label);
    },
    getSlugForLeaf(group, leaf) {
      const suffixParts = String(leaf || '').split(' › ').map((p) => p.trim()).filter(Boolean);
      const fullParts = [...(group.pathParts || []), ...suffixParts];
      return this.slugifyCategory(fullParts.join(' - '));
    },
    getCategoryLeafLabelById(categoryId) {
      const categoryLabel = this.getCategoryLabelById(categoryId);
      if (!categoryLabel) return '';

      const parts = categoryLabel
          .split(' - ')
          .map((part) => part.trim())
          .filter(Boolean);

      return parts[parts.length - 1] || '';
    },
    getProductCategoryGroups(content) {
      const categoryIds = Array.isArray(content?.category_ids)
          ? content.category_ids
          : [];

      const categoryPaths = categoryIds
          .map((categoryId) => {
            const categoryLabel = this.getCategoryLabelById(categoryId);
            if (!categoryLabel) return [];

            return categoryLabel
                .split(' - ')
                .map((part) => part.trim())
                .filter(Boolean);
          })
          .filter((parts) => parts.length);

      const uniqueCategoryPaths = [];
      categoryPaths.forEach((pathParts) => {
        const pathKey = pathParts.join(' - ');
        if (!uniqueCategoryPaths.some((entry) => entry.pathKey === pathKey)) {
          uniqueCategoryPaths.push({ pathKey, pathParts });
        }
      });

      const groupedPaths = [];

      uniqueCategoryPaths.forEach(({ pathParts }) => {
        const groupKey = pathParts.slice(0, -1).join(' - ') || pathParts[0];
        let group = groupedPaths.find((entry) => entry.groupKey === groupKey);
        if (!group) {
          group = {
            groupKey,
            paths: [],
          };
          groupedPaths.push(group);
        }

        group.paths.push(pathParts);
      });

      return groupedPaths.map((group) => {
        const commonPrefix = group.paths.reduce((prefix, pathParts) => {
          if (!prefix.length) return pathParts.slice();

          let index = 0;
          while (index < prefix.length && index < pathParts.length && prefix[index] === pathParts[index]) {
            index += 1;
          }

          return prefix.slice(0, index);
        }, []);

        const pathParts = commonPrefix.length ? commonPrefix : group.paths[0];
        const leafLabels = [];

        group.paths.forEach((pathPartsItem) => {
          const suffixParts = pathPartsItem.slice(pathParts.length).filter(Boolean);
          const leaf = suffixParts.join(' › ');
          if (leaf && !leafLabels.includes(leaf)) {
            leafLabels.push(leaf);
          }
        });

        return {
          pathParts,
          leafLabels,
        };
      }).filter((group) => group.pathParts.length || group.leafLabels.length);
    },
    getProductCategoryItems(content) {
      const categoryIds = Array.isArray(content?.category_ids)
          ? content.category_ids
          : [];

      return [
        {
          id: categoryIds[0],
          label: this.getCategoryLabelById(categoryIds[0]),
          slug: this.getCategorySlugById(categoryIds[0]),
        },
        {
          id: categoryIds[1],
          label: this.getCategoryLabelById(categoryIds[1]),
          slug: this.getCategorySlugById(categoryIds[1]),
        },
        {
          id: categoryIds[2],
          label: this.getCategoryLabelById(categoryIds[2]),
          slug: this.getCategorySlugById(categoryIds[2]),
        },
      ].filter((item) => item.id && item.label && item.slug);
    },
    getCategorySlugLevels(content) {
      const categorySlug = this.getLinkCat(content) || '';
      const parts = categorySlug
          .split('/')
          .map((part) => part.trim())
          .filter(Boolean);

      return parts.map((_, index) => parts.slice(0, index + 1).join('/'));
    },
    getCategorySlugByLevel(content, level) {
      return this.getLinkCat(content) || '';
    },
    getLinkCat(content) {
      return this.$store.getters.getLinkCat(content);
    },
    setSelectedCategoryBySlug(slug) {
      if (!slug) {
        sessionStorage.removeItem('selectedCategory');
        return;
      }

      const exactCategory = this.categories.find((cat) => this.slugifyCategory(cat.name || '') === slug);
      if (exactCategory) {
        sessionStorage.setItem('selectedCategory', exactCategory.name);
        return;
      }

      const groupedCategory = this.categories.find((cat) => {
        const parent = this.slugifyCategory((cat.name || '').split(' - ')[0] || '');
        return parent === slug;
      });

      if (groupedCategory) {
        const parentName = (groupedCategory.name || '').split(' - ')[0]?.trim();
        if (parentName) {
          sessionStorage.setItem('selectedCategory', parentName);
        }
        return;
      }

      const subgroupCategory = this.categories.find((cat) => {
        const parts = (cat.name || '').split(' - ').map((part) => part.trim()).filter(Boolean);
        if (parts.length < 2) {
          return false;
        }

        const subgroupSlug = this.slugifyCategory(`${parts[0]} - ${parts[1]}`);
        return subgroupSlug === slug;
      });

      if (subgroupCategory) {
        const parts = (subgroupCategory.name || '').split(' - ').map((part) => part.trim()).filter(Boolean);
        if (parts.length >= 2) {
          sessionStorage.setItem('selectedCategory', `${parts[0]} - ${parts[1]}`);
        }
      }
    },
    showToastSuccess(value) {
      toast.success(value, {
        autoClose: 3000
      })
    },
      showToastError(value) {
        toast.error(value, {
          autoClose: 3000
        })
      },
    openImageOverlay(index) {
      this.fullscreenIndex = Number(index) || 0;
      this.dialog = true;
    },
    closeImageOverlay() {
      this.dialog = false;
    }
  },
  mounted() {
    this.fetchAProduct(this.$route.params.id);
    console.log(this.choice);
  }
}
</script>

<style scoped>
.view-product-carousel :deep(.v-window),
.view-product-carousel :deep(.v-window__container),
.view-product-carousel :deep(.v-window-item),
.view-product-carousel-item {
  height: 100% !important;
}

.view-product-carousel {
  border-radius: 8px;
  overflow: hidden;
}

.view-product-carousel-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-product-image {
  width: 100%;
  height: 100%;
  cursor: zoom-in;
}

.view-product-image :deep(.v-img__img) {
  object-fit: contain !important;
  object-position: center center !important;
  cursor: zoom-in;
}

.thumbnail-gallery {
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.thumbnail-btn {
  width: 64px;
  height: 64px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease;
}

.thumbnail-btn:hover {
  border-color: #3b71f7;
  transform: translateY(-1px);
}

.thumbnail-btn-active {
  border-color: #3b71f7;
  box-shadow: 0 0 0 2px rgba(59, 113, 247, 0.15);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
}

.image-overlay {
  z-index: 3000;
}

.image-overlay :deep(.v-overlay__content) {
  width: 100vw;
  height: 100vh;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-content {
  position: relative;
  width: min(95vw, 1400px);
  height: min(92vh, 1000px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}

.overlay-close {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 3100;
  background: rgba(0, 0, 0, 0.45);
}

.overlay-image {
  width: 100%;
  height: 100%;
}

.overlay-carousel,
.overlay-carousel :deep(.v-window),
.overlay-carousel :deep(.v-window__container),
.overlay-carousel :deep(.v-window-item),
.overlay-carousel-item {
  width: 100%;
  height: 100% !important;
}

.overlay-carousel {
  border-radius: 8px;
  overflow: hidden;
  flex: 1 1 auto;
}

.overlay-thumbnail-gallery {
  width: 100%;
  justify-content: center;
}

.overlay-thumbnail-btn {
  background: rgba(255, 255, 255, 0.95);
}

.overlay-carousel-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-carousel-item .overlay-image {
  width: 100%;
  height: 100%;
}

.overlay-image :deep(.v-img__img) {
  object-fit: contain !important;
  object-position: center center !important;
}

.cat-link {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.product-category-group {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.product-category-level-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.product-category-pill {
  font-size: 15px;
  font-weight: 700;
  color: #1f2d3d;
  text-decoration: none;
  letter-spacing: 0.01em;
  background: #eef4ff;
  border: 1px solid #dbe8ff;
  border-radius: 999px;
  padding: 4px 10px;
}

.product-category-main {
  font-size: 15px;
  font-weight: 700;
  color: #1f2d3d;
  background: #eef4ff;
  border: 1px solid #dbe8ff;
  border-radius: 999px;
  padding: 4px 10px;
}

.product-category-sub {
  font-size: 13px;
  font-weight: 400;
  color: #5d6b7a;
  background: #f7f9fc;
  border: 1px solid #e7edf6;
  border-radius: 999px;
  padding: 3px 9px;
}

.product-category-extra {
  margin-left: 4px;
}

.product-category-arrow {
  color: #93a1b2;
  font-size: 12px;
  font-weight: 700;
}

.product-category-main:hover,
.product-category-sub:hover {
  text-decoration: underline;
}

.purchase-config {
  display: grid;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 18px;
}

.purchase-footer {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
}

.purchase-price-block {
  display: grid;
  justify-items: start;
  gap: 4px;
}

.purchase-price-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.purchase-price-row {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
}

.purchase-price-value,
.purchase-price-range {
  font-size: 2.1rem;
  font-weight: 800;
  line-height: 1;
  color: #111827;
}

.purchase-price-value--discount {
  color: #e53935;
}

.purchase-price-currency--discount {
  color: #e53935;
}

.purchase-price-old {
  color: rgba(17, 24, 39, 0.5);
  font-size: 1rem;
  font-weight: 600;
  text-decoration: line-through;
  align-self: center;
}

.purchase-price-currency {
  font-size: 1.55rem;
  font-weight: 700;
  line-height: 1;
  color: #111827;
}

.product-cart-btn {
  margin-top: 8px;
  border-radius: 999px !important;
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 10px 20px rgba(59, 113, 247, 0.2) !important;
}

.product-cart-btn:hover {
  transform: translateY(-1px);
}

.product-cart-btn :deep(.v-btn__content) {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.product-cart-btn :deep(.v-btn__overlay) {
  opacity: 0 !important;
}

.config-error {
  margin-top: 2px;
  color: #d32f2f;
  font-size: 0.82rem;
  font-weight: 600;
}

.config-card {
  display: grid;
  gap: 8px;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

.config-label {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.quantity-picker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.quantity-input-wrap {
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
  flex: 0 0 48px;
}

.quantity-input {
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  outline: none;
}

.option-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-pill {
  min-width: 92px;
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 999px;
  background: #ffffff;
  color: #1f2d3d;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}

.option-pill:hover {
  transform: translateY(-1px);
  border-color: rgba(59, 113, 247, 0.35);
}

.option-pill--active {
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(59, 113, 247, 0.2);
}

.option-pill--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.confirmation-dialog-card {
  min-width: min(420px, 92vw);
  border-radius: 22px !important;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.18) !important;
}

.confirmation-dialog-card :deep(.v-toolbar) {
  padding-inline: 6px;
}

.confirmation-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.confirmation-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.confirmation-summary {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
  border: 1px solid rgba(59, 113, 247, 0.14);
}

.summary-product-chip {
  width: 100%;
  padding: 2px 2px 6px;
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.35;
  color: #111827;
}

.summary-topline {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.summary-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.summary-label {
  flex: 1 1 auto;
  font-size: 0.86rem;
  color: #5d6b7a;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffffff;
  color: #111827;
  border: 1px solid rgba(17, 24, 39, 0.08);
  font-size: 0.86rem;
  font-weight: 700;
  justify-self: end;
}

.summary-chip--count {
  min-width: 40px;
  min-height: 40px;
  padding: 0 14px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.summary-price-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 10px 18px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.summary-price-value {
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
  color: #111827;
}

.summary-price-currency {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(59, 113, 247, 0.08);
  color: #3b71f7;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.summary-meta {
  display: grid;
  gap: 10px;
  padding-top: 4px;
}

.summary-price-pill--bottom {
  margin-top: 4px;
}

@media (max-width: 820px) {
  .elevation-5 {
    box-shadow: none !important;
  }
  .pa-5 {
    padding-bottom: 20px !important;
  }
  .summary-price-pill {
    min-height: 44px;
    padding-inline: 14px;
  }
  .summary-price-value {
    font-size: 1.1rem;
  }
  .quantity-picker {
    gap: 10px;
  }
  .purchase-price-value,
  .purchase-price-range {
    font-size: 1.7rem;
  }
  .purchase-price-currency {
    font-size: 1.25rem;
  }
  .option-pill {
    flex: 0 1 auto;
  }
}

</style>